import React, { useState } from 'react';
import { RecordItem } from '../../models/RecordItem';
import { Button, Input, TextArea, CurrencySelect } from '../ui';

interface CreateRecordModalProps {
  onClose: () => void;
  onCreate: (recordData: Partial<RecordItem>) => void;
  isLoading?: boolean;
}

const CreateRecordModal: React.FC<CreateRecordModalProps> = ({ onClose, onCreate, isLoading = false }) => {
  const [record_name, setRecordName] = useState('');
  const [record_description, setRecordDescription] = useState('');
  const [record_currency_code, setRecordCurrencyCode] = useState('USD');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const [errors, setErrors] = useState<{
    record_name?: string;
    record_description?: string;
    record_currency_code?: string;
  }>({});

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!record_name.trim()) {
      newErrors.record_name = 'Record name is required';
    } else if (record_name.length > 100) {
      newErrors.record_name = 'Record name must be less than 100 characters';
    }

    if (record_description.length > 500) {
      newErrors.record_description = 'Description must be less than 500 characters';
    }

    if (!record_currency_code) {
      newErrors.record_currency_code = 'Currency is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare record data
      const recordData: Partial<RecordItem> = {
        record_name: record_name.trim(),
        record_description: record_description.trim() || undefined,
        record_currency_code,
        created_at: new Date().toISOString(),
        // These would typically be set by the backend
        record_creator_id: 1, // Mock user ID - replace with actual user ID from context
        record_invite_code: Math.floor(100000 + Math.random() * 900000), // Generate random 6-digit invite code
      };

      // Call the onCreate callback
      await onCreate(recordData);

      // Reset form
      setRecordName('');
      setRecordDescription('');
      setRecordCurrencyCode('USD');
      setErrors({});

    } catch (error) {
      console.error('Error creating record:', error);
      // TODO: Show error message to user
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const submitting = isSubmitting || isLoading;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-700 bg-opacity-50 p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-light rounded-lg shadow-xl max-w-md w-full max-h-[100vh] h-full overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="relative p-6 pb-2 border-b border-grey_light">
          <div>
            <h3 className="text-xl text-dark">New Record</h3>
            <p className="text-grey_dark mt-1 text-sm">Create a new record for a person, business, or entity.</p>
          </div>

          <button
            onClick={onClose}
            className="absolute top-0 right-2 text-grey_dark hover:text-dark text-2xl p-2"
            aria-label="Close"
            disabled={submitting}
          >
            ×
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-3">
          <div className="space-y-3">
            {/* Record Name Field */}
            <div>
              <label htmlFor="record_name" className="block text-sm text-dark mb-2">
                Record Name *
              </label>
              <Input
                type="text"
                attributes={{
                  id: 'record_name',
                  value: record_name,
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => setRecordName(e.target.value),
                  placeholder: 'Enter the record name',
                  maxLength: 100,
                  disabled: submitting,
                }}
                className={`text-sm ${errors.record_name ? 'border-danger' : ''}`}
              />
              {errors.record_name && (
                <p className="mt-1 text-sm text-danger">{errors.record_name}</p>
              )}
              <p className="mt-1 text-xs text-end text-grey_dark">
                Max 100 characters.
              </p>
            </div>

            {/* Record Description Field */}
            <div>
              <label htmlFor="record_description" className="block text-sm text-dark mb-2">
                Description
              </label>
              <TextArea
                attributes={{
                  id: 'record_description',
                  value: record_description,
                  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => setRecordDescription(e.target.value),
                  placeholder: 'Describe what this record is for (optional)',
                  disabled: submitting,
                }}
                maxLength={500}
                showCharacterCount={true}
                className={`text-sm ${errors.record_description ? 'border-danger' : ''}`}
              />
              {errors.record_description && (
                <p className="mt-1 text-sm text-danger">{errors.record_description}</p>
              )}
            </div>

            {/* Currency Selection Field */}
            <div>
              <label htmlFor="record_currency_code" className="block text-sm text-dark mb-2">
                Currency *
              </label>
              <CurrencySelect
                value={record_currency_code}
                onChange={setRecordCurrencyCode}
                placeholder="Select a currency"
                disabled={submitting}
                error={!!errors.record_currency_code}
                className="text-sm"
                showCommonFirst={true}
                showFlags={true}
                searchable={true}
              />
              {errors.record_currency_code && (
                <p className="mt-1 text-sm text-danger">{errors.record_currency_code}</p>
              )}
            </div>

            {/* More Information Button */}
            <div className="pt-2">
              <button
                type="button"
                onClick={() => setShowMoreInfo(!showMoreInfo)}
                className="flex items-center text-sm text-grey_dark hover:text-grey_dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={submitting}
              >
                <span className="mr-2">{showMoreInfo ? '−' : '+'}</span>
                {showMoreInfo ? 'Less Information' : 'More Information'}
              </button>
            </div>

            {/* Information Section - Hidden by default */}
            {showMoreInfo && (
              <div className="border border-secondary_light rounded-lg p-4">
                <h4 className="text-sm font-medium text-secondary mb-2">What is a Record?</h4>
                <p className="text-xs text-secondary">
                  A record is a container for your financial data. You can create separate records for different purposes like personal finances, business accounts, or specific projects. Each record has its own transactions, accounts, and permissions.
                  <p >Select the primary currency for this record. This determines how amounts are displayed.</p>
                </p>
              </div>
            )}
          </div>
        </form>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-grey_light bg-light">
          <div className="flex justify-between space-x-3">
            <Button
              type="button"
              variant="google"
              onClick={onClose}
              disabled={submitting}
              className="min-w-[100px] text-grey_dark"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="filled-primary"
              onClick={handleSubmit}
              disabled={submitting || !record_name.trim()}
              className="min-w-[100px]"
            >
              {submitting ? 'Creating...' : 'Create Record'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateRecordModal;